import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  type ReactNode,
} from 'react'
import { Button, Modal } from 'antd'
import { ExclamationCircleFilled } from '@ant-design/icons'
import { useLocation } from 'react-router-dom'
import { API_EVENTS } from '@/constants'
import { ToastContext, type ToastContextValue, type ToastType } from './toastContext'

interface ToastItem {
  id: number
  message: string
  type: ToastType
  exiting: boolean
  routeSignature: string
}

const TYPE_STYLES: Record<ToastType, string> = {
  success: 'bg-gray-900 text-white',
  error: 'bg-gray-900 text-white',
  warning: 'bg-gray-900 text-white',
  info: 'bg-gray-900 text-white',
}

const MAX_VISIBLE = 3

const ICONS: Record<ToastType, string> = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
}

function ToastContainer({ items, onDismiss }: { items: ToastItem[]; onDismiss: (id: number) => void }) {
  return (
    <div className="fixed bottom-6 left-1/2 z-[9999] flex -translate-x-1/2 flex-col-reverse items-center gap-2 pointer-events-none">
      {items.map((item) => {
        const isError = item.type === 'error'
        return (
          <div
            key={item.id}
            className={`pointer-events-auto flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-medium shadow-lg transition-all duration-300 ${
              isError ? '' : 'cursor-pointer'
            } ${TYPE_STYLES[item.type]} ${item.exiting ? 'translate-y-4 opacity-0' : 'translate-y-0 opacity-100'}`}
            style={{ animation: item.exiting ? undefined : 'slideUp 0.3s ease-out' }}
            onClick={isError ? undefined : () => onDismiss(item.id)}
            role="alert"
          >
            <span className="text-base leading-none">{ICONS[item.type]}</span>
            <span>{item.message}</span>
            {isError && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onDismiss(item.id)
                }}
                className="ml-2 flex h-5 w-5 items-center justify-center rounded text-base leading-none text-white/80 hover:bg-white/15 hover:text-white"
                aria-label="閉じる"
              >
                ×
              </button>
            )}
          </div>
        )
      })}
      <style>{`@keyframes slideUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  )
}

function SystemErrorDialog({ message, onClose }: { message: string | null; onClose: () => void }) {
  return (
    <Modal
      open={Boolean(message)}
      centered
      closable={false}
      mask={{ closable: false }}
      keyboard={false}
      width={460}
      zIndex={10050}
      title={
        <div className="flex items-center gap-2 text-base font-semibold text-gray-800">
          <ExclamationCircleFilled className="text-xl text-red-500" />
          <span>エラー</span>
        </div>
      }
      footer={
        <div className="flex justify-end">
          <Button type="primary" danger onClick={onClose}>
            閉じる
          </Button>
        </div>
      }
      destroyOnHidden
    >
      <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-800">{message}</p>
      </div>
    </Modal>
  )
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const routeSignature = `${location.pathname}${location.search}${location.hash}`
  const [items, setItems] = useState<ToastItem[]>([])
  const [systemErrorMessage, setSystemErrorMessage] = useState<string | null>(null)
  const nextId = useRef(0)
  const systemErrorOpenRef = useRef(false)
  const visibleItems = useMemo(
    () => items.filter((item) => item.routeSignature === routeSignature),
    [items, routeSignature],
  )

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)))
    setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), 300)
  }, [])

  const addToast = useCallback(
    (text: string, type: ToastType = 'success') => {
      if (type === 'error' && systemErrorOpenRef.current) {
        return
      }
      const id = ++nextId.current
      setItems((prev) => {
        // Deduplicate: skip if same message already visible
        if (prev.some((t) => t.message === text && t.routeSignature === routeSignature && !t.exiting)) return prev
        const next = [...prev, { id, message: text, type, exiting: false, routeSignature }]
        // Auto-dismiss oldest when exceeding max
        const active = next.filter((t) => !t.exiting && t.routeSignature === routeSignature)
        if (active.length > MAX_VISIBLE) {
          const oldest = active[0]
          return next.map((t) => (t.id === oldest.id ? { ...t, exiting: true } : t))
        }
        return next
      })
      if (type !== 'error') {
        setTimeout(() => dismiss(id), 3000)
      }
    },
    [dismiss, routeSignature],
  )

  const value = useMemo<ToastContextValue>(() => ({
    toast: addToast,
    success: (msg: string) => addToast(msg, 'success'),
    error: (msg: string) => addToast(msg, 'error'),
    warning: (msg: string) => addToast(msg, 'warning'),
    info: (msg: string) => addToast(msg, 'info'),
  }), [addToast])

  useEffect(() => {
    const handler = (e: Event) => {
      const text = (e as CustomEvent<{ message: string }>).detail?.message
      if (text) {
        systemErrorOpenRef.current = true
        setItems([])
        setSystemErrorMessage(text)
      }
    }
    window.addEventListener(API_EVENTS.API_ERROR, handler)
    return () => window.removeEventListener(API_EVENTS.API_ERROR, handler)
  }, [])

  useEffect(() => {
    const handler = (e: Event) => {
      const text = (e as CustomEvent<{ message: string }>).detail?.message
      if (text) addToast(text, 'warning')
    }
    window.addEventListener('app:pending-alert', handler)
    return () => window.removeEventListener('app:pending-alert', handler)
  }, [addToast])

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setItems((prev) => prev.filter((item) => item.routeSignature === routeSignature))
    }, 0)
    return () => window.clearTimeout(timeout)
  }, [routeSignature])

  const closeSystemError = useCallback(() => {
    systemErrorOpenRef.current = false
    setSystemErrorMessage(null)
  }, [])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer items={visibleItems} onDismiss={dismiss} />
      <SystemErrorDialog message={systemErrorMessage} onClose={closeSystemError} />
    </ToastContext.Provider>
  )
}
